import * as THREE from 'three'
import { describeDepthError, readDepth, type DepthFrame } from '../depth/depth-frame'
import { CameraMotion } from '../depth/camera-motion'
import { FloorPlane } from '../depth/floor-plane'
import { HitPlane } from '../depth/hit-plane'
import { lockGround, stripToRelief, type GroundReport } from '../depth/ground'
import type { SurfaceProbeInput } from '../depth/surface-probe'
import { Bodies } from './bodies'
import {
  buildTuning,
  cubeSize,
  DEFAULT_CONFIG,
  type DemoConfig,
  type FloorMode,
} from './config'
import { Controls } from './controls'
import type { DemoContext } from './context'
import { Occluder } from './occluder'
import { viewCentreWorld } from './reticle'
import { Skin, SKIN_LEGEND } from './skin'
import { writeDepthProblem, writeFrameStats } from './stats'

/**
 * The demo's lifecycle and the body of its frame loop.
 *
 * Order within a frame matters and is not arbitrary: the floor is resolved
 * before anything is dropped or simulated, so a box and the solver see the same
 * ground; the occluder mesh and the skin are rebuilt on a slower clock than the
 * solver, because they are what a person looks at rather than what the boxes
 * stand on.
 */

/** The mesh is what you see, not what the boxes stand on; 5 Hz is enough. */
const OCCLUDER_INTERVAL_MS = 200

/** A depth read can miss for a few frames without the world having gone away. */
const MISS_GRACE_MS = 700

/** A hit older than this is the reticle off the floor, not the floor gone. */
const HIT_STALE_MS = 2000

const START_NOTE = 'Point at a textured surface, move the phone 20–30 cm, then tap Drop.'

const scratchPoint = new THREE.Vector3()

export class DemoScene {
  private readonly ctx: DemoContext
  private readonly occluder: Occluder
  private readonly skin: Skin
  private readonly bodies: Bodies
  private readonly floorPlane: FloorPlane
  private readonly hitPlane = new HitPlane()
  private readonly motion = new CameraMotion()
  private readonly controls: Controls

  private config: DemoConfig = DEFAULT_CONFIG
  private floor: GroundReport | null = null
  /** Hit-test has stopped returning results, so the plane is coasting. */
  private floorStale = false

  private pendingDrops = 0
  private lastOccluderAt = 0
  private lastDepthAt = 0
  private lastFrameAt = 0
  private depthProblem = false

  private refSpace: XRReferenceSpace | null = null
  // three.js registers no `reset` listener of its own, so a re-localisation
  // would silently move every box relative to the world.
  private readonly onSpaceReset = (): void => {
    this.bodies.clear()
  }

  constructor(ctx: DemoContext) {
    this.ctx = ctx
    this.occluder = new Occluder(ctx.scene)
    this.skin = new Skin(ctx.scene, this.occluder)
    this.bodies = new Bodies(ctx.scene)
    this.floorPlane = new FloorPlane(ctx.scene)

    this.controls = new Controls(ctx.hud, this.config, {
      onDrop: () => {
        this.pendingDrops += 1
      },
      onClear: () => this.bodies.clear(),
      onCalibrate: () => this.bodies.calibrate(),
      onChange: (config) => this.applyConfig(config),
    })

    this.applyConfig(this.config)
  }

  reset(): void {
    this.bodies.reset()
    this.occluder.clear()
    this.hitPlane.reset()
    this.motion.reset()
    this.floorPlane.hide()
    this.floor = null
    this.pendingDrops = 0
    this.lastOccluderAt = 0
    this.lastDepthAt = 0
    this.lastFrameAt = 0
    this.depthProblem = false
    this.config = DEFAULT_CONFIG
    this.controls.sync(this.config)
    this.applyConfig(this.config)
  }

  update(frame: XRFrame, refSpace: XRReferenceSpace): void {
    this.watchReferenceSpace(refSpace)

    const now = performance.now()
    const result = readDepth(frame, refSpace, this.ctx.session)

    if (!result.ok) {
      const holding = this.lastDepthAt > 0 && now - this.lastDepthAt < MISS_GRACE_MS
      writeDepthProblem(this.ctx.hud, describeDepthError(result.reason), holding)
      if (!holding && !this.depthProblem) {
        this.ctx.hud.setNote(describeDepthError(result.reason), 'bad')
        this.depthProblem = true
      }
      // The boxes simply freeze: no depth is no ground, and holding a stale
      // height is the world memory this demo is built to do without.
      this.lastFrameAt = now
      return
    }

    this.lastDepthAt = now
    if (this.depthProblem) {
      this.depthProblem = false
      this.updateNote()
    }

    const depth = result.frame
    this.occluder.unproject(depth)
    this.motion.update(depth.view.transform.matrix, now)
    this.applyFloor(depth, frame, refSpace, now)

    while (this.pendingDrops > 0) {
      this.pendingDrops -= 1
      this.drop(depth)
    }

    const delta = this.lastFrameAt === 0 ? 0 : (now - this.lastFrameAt) / 1000
    this.lastFrameAt = now
    this.bodies.simulate(this.probeInput(), buildTuning(this.config, this.bodies.bias), delta)
    this.bodies.syncMeshes()

    if (now - this.lastOccluderAt >= OCCLUDER_INTERVAL_MS) {
      this.lastOccluderAt = now
      this.occluder.rebuild()
      this.paintSkin(depth)
    }

    if (this.config.stats) this.writeStats(depth, now)
  }

  dispose(): void {
    this.refSpace?.removeEventListener('reset', this.onSpaceReset)
    this.refSpace = null

    this.bodies.dispose()
    this.skin.dispose()
    this.occluder.dispose()
    this.floorPlane.dispose()
    this.hitPlane.dispose()
  }

  private applyConfig(config: DemoConfig): void {
    this.config = config
    this.skin.setMode(config.skin)
    this.ctx.hud.setMetricsVisible(config.stats)
    this.updateNote()
  }

  private updateNote(): void {
    this.ctx.hud.setNote(this.config.skin === 'off' ? START_NOTE : SKIN_LEGEND)
  }

  private probeInput(): SurfaceProbeInput {
    return {
      positions: this.occluder.positions,
      valid: this.occluder.metres,
      cells: this.occluder.cells,
      ground: this.floor?.plane ?? null,
    }
  }

  /**
   * Resolves the ground for this frame, before anything is dropped or solved.
   *
   * In `hit` mode there is no fit at all: the plane is ARCore's own trackable
   * and every cell not standing proud of it is deleted outright, which is what
   * makes the floor flat by construction. Until the first hit arrives this does
   * nothing, so the demo degrades to `raw` rather than to a floorless state.
   */
  private applyFloor(
    depth: DepthFrame,
    frame: XRFrame,
    refSpace: XRReferenceSpace,
    now: number
  ): void {
    const mode: FloorMode = this.config.floor

    if (mode === 'hit') {
      this.applyHitFloor(frame, refSpace, now)
      return
    }

    this.floorPlane.hide()

    const dirs = this.occluder.rayDirections
    if (mode === 'raw' || !dirs) {
      this.floor = null
      return
    }

    if (!this.seedPoint(depth, scratchPoint)) return

    this.floor = lockGround(
      this.occluder.positions,
      this.occluder.metres,
      this.occluder.cells,
      this.occluder.gridWidth,
      this.occluder.gridHeight,
      scratchPoint.x,
      scratchPoint.z,
      dirs,
      depth.view.transform.matrix,
      this.occluder.flags,
      false,
      this.floor?.plane ?? null
    )
  }

  private applyHitFloor(frame: XRFrame, refSpace: XRReferenceSpace, now: number): void {
    const plane = this.hitPlane.update(frame, refSpace, this.ctx.session, now)
    this.floorStale = this.hitPlane.state(now, HIT_STALE_MS) === 'stale'
    if (!plane) {
      this.floor = null
      this.floorPlane.hide()
      return
    }

    const valid = this.occluder.cells - this.occluder.emptyCells
    const proud = stripToRelief(
      this.occluder.positions,
      this.occluder.metres,
      this.occluder.cells,
      plane
    )

    // `locked` carries the same meaning it does in the fitted path — cells the
    // floor accounted for — so the stats row reads the same way in both modes.
    this.floor = { plane, locked: valid - proud, filled: 0, valid, radial: 0, spikes: 0 }
    this.floorPlane.sync(plane, this.config.skin)
  }

  /**
   * Where to seed the fitted floor. Following the last box keeps the plane
   * under the thing being watched, but a box that has slid out of the depth
   * frame would put the seed disc on nothing and kill the fit for that frame,
   * so an unsupported box falls back to the crosshair.
   */
  private seedPoint(depth: DepthFrame, out: THREE.Vector3): boolean {
    const anchor = this.bodies.anchor
    if (anchor && anchor.mode !== 'unsupported') {
      out.set(anchor.x, anchor.y, anchor.z)
      return true
    }
    return viewCentreWorld(depth, out)
  }

  private drop(depth: DepthFrame): void {
    if (this.bodies.full) return
    if (!viewCentreWorld(depth, scratchPoint)) return
    this.bodies.drop(scratchPoint.x, scratchPoint.y, scratchPoint.z, cubeSize(this.config))
  }

  private paintSkin(depth: DepthFrame): void {
    if (this.config.skin === 'off') return

    const anchor = this.bodies.anchor
    if (anchor) scratchPoint.set(anchor.x, anchor.y, anchor.z)
    else if (!viewCentreWorld(depth, scratchPoint)) return

    this.skin.paint(
      this.occluder,
      this.floor?.plane ?? null,
      buildTuning(this.config, this.bodies.bias).climbMetres,
      scratchPoint.x,
      scratchPoint.z
    )
  }

  private writeStats(depth: DepthFrame, now: number): void {
    writeFrameStats(this.ctx.hud, {
      depth: `${depth.width}x${depth.height} ${depth.format}`,
      emptyCells: this.occluder.emptyCells,
      cells: this.occluder.cells,
      floorMode: this.config.floor,
      floor: this.floor,
      floorStale: this.config.floor === 'hit' && this.floorStale,
      bodies: this.bodies.snapshot(),
      stillSeconds: this.motion.stillSeconds(now),
      moving: this.motion.moving,
    })
  }

  private watchReferenceSpace(refSpace: XRReferenceSpace): void {
    if (this.refSpace === refSpace) return
    this.refSpace?.removeEventListener('reset', this.onSpaceReset)
    refSpace.addEventListener('reset', this.onSpaceReset)
    this.refSpace = refSpace
  }
}
